/**
 * Russian pluralisation helper — Phase 4 W3.T3.2 (M-2 fix).
 *
 * Implements the standard ru-CLDR plural rule for cardinal numbers:
 *
 *   one  → n % 10 === 1 AND n % 100 !== 11
 *          (1, 21, 31, 101, 121, 1001, …)
 *
 *   few  → n % 10 ∈ [2, 3, 4] AND n % 100 ∉ [12, 13, 14]
 *          (2-4, 22-24, 32-34, …)
 *
 *   many → everything else
 *          (0, 5-20, 25-30, 100, 111, 112-114, …)
 *
 * Rationale:
 *   - The ceiling rule («n % 100 ∈ 11..14 → many») has higher precedence
 *     than the units rule. `12` is `many` even though `12 % 10 === 2`.
 *   - Negative numbers reuse the absolute-value bucket — «-1 проект» reads
 *     more naturally in UI than «-1 проектов» when the upstream calc emits
 *     a delta. Callers that don't want this can call `Math.max(0, n)` or
 *     pass `Math.abs(n)` themselves; the helper itself is pure.
 *   - Non-finite inputs (NaN, ±Infinity) fall through to `many` — there is
 *     no sensible plural for them; the alternative would be throwing,
 *     which is hostile to UI rendering paths that do `pluralRu(rows.length)`
 *     before checking that `rows` is loaded.
 *
 * Invariants:
 *   - Pure, total, deterministic. No I/O.
 *   - Constant-time. O(1) regardless of magnitude.
 */

/**
 * Pick the correct Russian plural form for `n`.
 *
 * @param n     Cardinal count. Negatives and non-finites are accepted; see
 *              module docstring for behaviour.
 * @param forms `[one, few, many]` — exactly three Russian word forms.
 *              Example: `['проект', 'проекта', 'проектов']`.
 *
 * @example
 *   pluralRu(1, ['проект', 'проекта', 'проектов']) // → 'проект'
 *   pluralRu(2, ['проект', 'проекта', 'проектов']) // → 'проекта'
 *   pluralRu(5, ['проект', 'проекта', 'проектов']) // → 'проектов'
 *   pluralRu(11, ['проект', 'проекта', 'проектов']) // → 'проектов'
 *   pluralRu(21, ['проект', 'проекта', 'проектов']) // → 'проект'
 */
export function pluralRu(
  n: number,
  forms: readonly [one: string, few: string, many: string],
): string {
  // Non-finite or NaN — no sensible plural; default to "many" to avoid
  // throwing in render paths.
  if (!Number.isFinite(n)) return forms[2];

  // ru-CLDR is symmetric across sign — bucket by absolute value.
  // `Math.trunc` (not `Math.floor`) preserves sign-symmetry around 0.
  const absN = Math.abs(Math.trunc(n));

  const mod100 = absN % 100;
  // Ceiling rule first: 11..14 are always "many" regardless of last digit.
  if (mod100 >= 11 && mod100 <= 14) return forms[2];

  const mod10 = absN % 10;
  if (mod10 === 1) return forms[0];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}
